import { MouseEvent, useCallback, useEffect, useState } from "react";
import useMenuStore from "src/store/useMenuStore";
import { MenuDto } from "src/dtos/menu/menuDto";
import useUiuxStore from "src/store/useUiuxStore";
import { actionBarMinWidthWrapper } from "src/components/common/common";
import { TabUtils } from "src/utils/menu/tabUtils";
import useUserStore from "src/store/useUserStore";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";
import { useDescText } from "src/hooks/useDescText";
import { DESC_BTN } from "src/utils/descLanguage/descLanguageUtil";
import { postProgramLog } from "src/apis/programLogApi";
import { MenuOutlined } from "@ant-design/icons";

interface AuthProps {
	queryAut?: string;
	insertAut?: string;
	deleteAut?: string;
	saveAut?: string;
	printAut?: string;
	excelAut?: string;
	initAut?: string;
	page?: number;
	allCount: number;
}

type ActionbarClickHandler = (e: MouseEvent<HTMLButtonElement>) => void | Promise<void>;

interface Props {
	onFetchClick?: () => void;
	onCreateClick?: ActionbarClickHandler;
	onDeleteClick?: () => void;
	onInitClick?: () => void;
	onSaveClick?: () => void;
	onPrintClick?: () => void;
	onExcelClick?: () => void;
	page?: number;
	isDashboard?: boolean;
	customButtons?: React.ReactNode[];
}

const config = {
	env: process.env.REACT_APP_MODE,
}

const Actionbar = ({ 
	onFetchClick, 
	onCreateClick, 
	onDeleteClick, 
	onInitClick, 
	onSaveClick, 
	onPrintClick, 
	onExcelClick,
	page = 1,
	isDashboard = false,
	customButtons = [],
}: Props) => {
	const { menuList } = useMenuStore();
	const {
		activeTab,
		authactionbarLogModalVisible,
		setAuthactionbarLogModalVisible,
		sidebarVisible,
		setSidebarVisible,
	} = useUiuxStore();
	const { userInfo } = useUserStore();
	const { handleExitClick } = TabUtils();
	const auth = useActionbarAuthStore((state) => state.auth);
	const { renderDescText } = useDescText();

	const [thirdMenu, setThirdMenu] = useState<MenuDto>();

	const buttonClassName = (isDisabled?: boolean) => 
		`flex h-[42px] min-w-[84px] items-center justify-center rounded-[9px] px-[20px] text-[15px] font-bold text-white transition-all ${
			isDisabled 
				? "cursor-not-allowed bg-[#8993A6]"
				: "bg-gradient-to-b from-[#2B63E8] to-[#16399E] shadow-[0_3px_8px_rgba(22,57,158,0.24)] hover:brightness-110 active:scale-[0.98]"
		}`;

	// 권한값을 불리언으로 변환하는 함수
	const isDisabled = (authValue?: string) => authValue !== "Y";

	useEffect(() => {
		if (!activeTab?.key) return;
		
		// 계층적 메뉴 구조에서 현재 활성 탭에 해당하는 메뉴 찾기 (모든 레벨 지원)
		let foundMenu: MenuDto | undefined;
		
		// 재귀적으로 메뉴를 찾는 함수
		const findMenuInHierarchy = (menus: MenuDto[], targetKey: string): boolean => {
			for (const menuItem of menus) {
				if (menuItem.menuCd === targetKey) {
					foundMenu = menuItem;
					return true;
				}
				
				// 하위 메뉴에서 재귀적으로 찾기
				if (menuItem.subMenu && menuItem.subMenu.length > 0) {
					if (findMenuInHierarchy(menuItem.subMenu, targetKey)) {
						return true;
					}
				}
			}
			return false;
		};
		
		// 1차 메뉴부터 시작하여 모든 레벨에서 찾기
		findMenuInHierarchy(menuList, activeTab.key);
		
		if (foundMenu) {
			setThirdMenu(foundMenu);
		}
	}, [activeTab, menuList])

	const logAction = useCallback((actionType: string) => {
		if (!thirdMenu?.menuCd) return;
		postProgramLog({ programId: thirdMenu.menuCd, actionType });
	}, [thirdMenu?.menuCd]);

	const getManualHelpUrls = useCallback(() => {
		const manualPageFromMenu = thirdMenu?.manualPage;
		const manualPageNumber =
			typeof manualPageFromMenu === 'number' && manualPageFromMenu > 0
				? manualPageFromMenu
				: page;

		const manualBaseUrl = 'http://helpmes.raonzena.co.kr';
		const manualUrl = `${manualBaseUrl}#page=${manualPageNumber}`;
		return { manualBaseUrl, manualUrl };
	}, [thirdMenu?.manualPage, page]);

	const { manualUrl } = getManualHelpUrls();

	return <div className={`h-[100px] flex flex-col px-[12px] py-[4px] bg-blue-lightest justify-center overflow-x-auto ${actionBarMinWidthWrapper}`}>
		<div className="flex items-center gap-4 mb-2"> {/* div로 감싸고 flex 적용 */}
			<button
				className="flex h-[40px] w-[44px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-b from-[#2B63E8] to-[#16399E] text-[20px] text-white shadow-[0_4px_10px_rgba(22,57,158,0.28)]"
				onClick={() => setSidebarVisible(!sidebarVisible)}
				aria-label="메뉴 숨기기/열기"
				title="메뉴 숨기기/열기"
			>
				<MenuOutlined />
			</button>
			<span className="text-black-900 font-bold text-[21px] whitespace-nowrap">
				{thirdMenu ? renderDescText(thirdMenu.menuCd, thirdMenu.menuNm) : ""} / {thirdMenu?.menuCd}
			</span>
		</div>
		<div className="flex flex-1 items-center justify-between">
			<div className="flex w-full items-center justify-between">
				<div className="flex items-center gap-[8px]">
					<button 
						className={buttonClassName(isDisabled(auth?.queryAut))}
						onClick={() => { logAction('SELECT'); onFetchClick?.(); }}
						disabled={isDisabled(auth?.queryAut)}>
						{renderDescText(DESC_BTN.FETCH, "조회")}
					</button>
					<button 
						className={buttonClassName(isDisabled(auth?.insertAut))}
						onClick={(e) => { logAction('INSERT'); onCreateClick?.(e); }}
						disabled={isDisabled(auth?.insertAut)}>
						{renderDescText(DESC_BTN.CREATE, "등록")}
					</button>
					<button 
						className={buttonClassName(isDisabled(auth?.deleteAut))}
						onClick={() => { logAction('DELETE'); onDeleteClick?.(); }}
						disabled={isDisabled(auth?.deleteAut)}>
						{renderDescText(DESC_BTN.DELETE, "삭제")}
					</button>
					<button 
						className={buttonClassName(isDisabled(auth?.saveAut))}
						onClick={() => { logAction('SAVE'); onSaveClick?.(); }}
						disabled={isDisabled(auth?.saveAut)}>
						{renderDescText(DESC_BTN.SAVE, "저장")}
					</button>
					<button 
						className={buttonClassName(isDisabled(auth?.printAut))}
						onClick={() => { logAction('PRINT'); onPrintClick?.(); }}
						disabled={isDisabled(auth?.printAut)}>
						{renderDescText(DESC_BTN.PRINT, "출력")}
					</button>
					<button 
						className={buttonClassName(isDisabled(auth?.excelAut))}
						onClick={() => { logAction('EXCEL'); onExcelClick?.(); }}
						disabled={isDisabled(auth?.excelAut)}>
						{renderDescText(DESC_BTN.EXCEL, "Excel")}
					</button>
					<button 
						className={buttonClassName(false)}
						onClick={onInitClick}
						disabled={false}>
						{renderDescText(DESC_BTN.RESET, "초기화")}
					</button>
					<button 
						className={ isDashboard ? 'hidden' : buttonClassName(false) }
						onClick={() => {
							handleExitClick(activeTab?.key!);
						}}>
						{renderDescText(DESC_BTN.CLOSE, "닫기")}
					</button>
					{/* 커스텀 버튼들 */}
					{customButtons.map((button, index) => (
						<div key={`custom-button-${index}`} className="h-full">
							{button}
						</div>
					))}
				</div>
				<button className={buttonClassName(false)}
					onClick={() => {
						window.open(manualUrl, '_blank');
					}}>
					{renderDescText(DESC_BTN.HELP, "도움말")} ?
				</button>
				
				{userInfo?.autGroupCd === "ADMIN" && config.env === "dev" && (
					<button 
						className={buttonClassName(false)}
						onClick={() => {
							setAuthactionbarLogModalVisible(!authactionbarLogModalVisible);
						}}
					>
						로그 보기
					</button>
				)}

			</div>
		</div>
	</div>
}

export default Actionbar;
